import { CanvassingSessionDoorVisitStatus, VoterDoorVisitAssociation } from '../../graphql/__generated__/graphql';
import {
  CANVASSER_DIRECTORY_LOADING_LABEL,
  CANVASSER_DIRECTORY_UNKNOWN_LABEL,
} from '../BrokerageCanvasserDirectoryContext';
import { getCanvassingSessionDoorVisitStatusLabel } from '../canvassing-sessions/canvassingSessionLabels';

export const VOTER_SEARCH_MAX_LENGTH = 100;
export const VOTER_STATE_FILTER_MAX_LENGTH = 10;
export const ALL_VOTER_STATES_VALUE = '';
export const ALL_VOTER_STATES_LABEL = 'All states';

export const voterStateFilterLabel = (value: string): string =>
  value.length > 0 ? value : ALL_VOTER_STATES_LABEL;
export const VOTER_SEARCH_DEBOUNCE_MS = 300;
export const VOTER_PAGE_SIZE_OPTIONS = [12, 25, 50] as const;
export const DEFAULT_VOTER_PAGE_SIZE = 25;
export const DEFAULT_VOTER_PAGE = 0;
export const MISSING_FIELD = '—';
export const UNNAMED_VOTER = 'Unnamed voter';
export const NO_HOUSEHOLD_LINKED = 'No household linked.';
export const NO_VOTERS_AVAILABLE = 'No voters available.';
export const NO_VOTERS_MATCH = 'No voters match your search.';
export const NO_ASSOCIATED_DOOR_VISITS = 'No associated door visits.';
export const VOTER_UNAVAILABLE = 'Voter unavailable.';
export const LOCATION_UNAVAILABLE = 'Location unavailable.';
export const MAP_UNAVAILABLE = 'Map unavailable';
export const VOTER_ASSOCIATION_EXPLANATION =
  'Matches indicate system associations, not confirmed contact with this voter. Household activity uses the current household.';
export const VOTER_SEARCH_OVERLENGTH_MESSAGE = 'Search must be 100 characters or fewer.';

export type VoterPageSize = (typeof VOTER_PAGE_SIZE_OPTIONS)[number];

export type VoterNameFields = {
  firstName?: string | null;
  lastName?: string | null;
};

export type VoterAddressFields = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

const doorVisitStatusByWireValue: Record<string, CanvassingSessionDoorVisitStatus> = {
  pending: CanvassingSessionDoorVisitStatus.Pending,
  submitting_p1: CanvassingSessionDoorVisitStatus.SubmittingP1,
  submitted_p1: CanvassingSessionDoorVisitStatus.SubmittedP1,
  submitting_p2: CanvassingSessionDoorVisitStatus.SubmittingP2,
  submitted_p2: CanvassingSessionDoorVisitStatus.SubmittedP2,
  no_speech: CanvassingSessionDoorVisitStatus.NoSpeech,
};

const associationLabels: Record<VoterDoorVisitAssociation, string> = {
  [VoterDoorVisitAssociation.DirectVoter]: 'Voter match',
  [VoterDoorVisitAssociation.CurrentHousehold]: 'Current household',
  [VoterDoorVisitAssociation.Both]: 'Voter match · Current household',
};

export const isVoterPageSize = (value: unknown): value is VoterPageSize =>
  value === 12 || value === 25 || value === 50;

export const isVoterStateFilterValue = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= VOTER_STATE_FILTER_MAX_LENGTH && value.trim() === value;

export const voterRegistrationStateOptions = (states: readonly string[], selected: string): string[] => {
  const options: string[] = [];
  const seen = new Set<string>();
  for (const state of states) {
    if (!isVoterStateFilterValue(state) || state.length === 0 || seen.has(state)) continue;
    seen.add(state);
    options.push(state);
  }
  if (selected.length > 0 && isVoterStateFilterValue(selected) && !seen.has(selected)) options.push(selected);
  return options;
};

export const trimmedText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const displayField = (value: unknown): string => {
  const trimmed = trimmedText(value);
  return trimmed || MISSING_FIELD;
};

export const displayVoterName = (voter: VoterNameFields): string => {
  const parts = [voter.firstName, voter.lastName].map(trimmedText).filter(part => part.length > 0);
  return parts.length > 0 ? parts.join(' ') : UNNAMED_VOTER;
};

export const formatHouseholdAddress = (household: VoterAddressFields | null | undefined): string[] => {
  if (household === null || household === undefined) return [];

  const street = [household.address1, household.address2].map(trimmedText).filter(part => part.length > 0);
  const city = trimmedText(household.city);
  const state = trimmedText(household.state);
  const zip = trimmedText(household.zip);
  const cityState = [city, state].filter(part => part.length > 0).join(', ');
  const locality = [cityState, zip].filter(part => part.length > 0).join(' ');

  return [...street, locality].filter(part => part.length > 0);
};

export const formatDoorVisitStatusLabel = (status: unknown): string => {
  const raw = typeof status === 'string' ? status.trim() : '';
  if (raw.length === 0) return MISSING_FIELD;

  const mapped = doorVisitStatusByWireValue[raw.toLowerCase()];
  return mapped === undefined ? raw : getCanvassingSessionDoorVisitStatusLabel(mapped);
};

export const formatVoterAssociationLabel = (association: VoterDoorVisitAssociation): string => associationLabels[association];

export const formatCanvasserLabel = (
  canvasserId: string | null | undefined,
  resolveLabel: (canvasserId: string) => string,
): string => {
  const id = trimmedText(canvasserId);
  if (id.length === 0) return MISSING_FIELD;

  const label = resolveLabel(id);
  if (label === CANVASSER_DIRECTORY_LOADING_LABEL) return label;
  if (label === CANVASSER_DIRECTORY_UNKNOWN_LABEL || label.trim().length === 0) return id;

  return label;
};

const isFiniteInRange = (value: unknown, minimum: number, maximum: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum;

export const hasDisplayableHouseholdCoordinates = (latitude: unknown, longitude: unknown): boolean =>
  isFiniteInRange(latitude, -90, 90) && isFiniteInRange(longitude, -180, 180);

export const readDisplayableHouseholdCoordinates = (
  latitude: unknown,
  longitude: unknown,
): { latitude: number; longitude: number } | null => {
  if (!isFiniteInRange(latitude, -90, 90) || !isFiniteInRange(longitude, -180, 180)) return null;
  return { latitude, longitude };
};
